/**
 * 💾 **작업 보관함** — 웹에 올리지 않고 하던 일만 저장한다.
 *
 * ## 왜 필요한가 (2026-08-21 요청)
 * 전자책은 한 번에 다 못 만든다(12쪽이면 그림 12장·자막 24개). 그런데 저장하는 길이
 * **[웹에 올리기] 하나뿐**이라, 중간에 저장하면 **미완성이 아이 화면에 나갔다.**
 * 안 하면 창을 닫는 순간 다 날아간다. 둘 다 나쁘다.
 *
 * ## 이 기기에만 남는다
 * 서버에 「미완성 보관용 표」를 새로 만들지 않았다. 작업 중인 원고는 **그 사람 것**이고,
 * 다른 기기에서 이어서 하는 일은 드물다. 대신 화면에 그렇게 적는다 —
 * "이 기기에 저장했어요"라고 말하지 않으면 다른 PC 에서 찾다가 잃어버린 줄 안다.
 *
 * ## 그림은 이미 서버에 있다
 * 그림·소리는 **올리는 순간 엣지에 저장된다**(게시와 별개). 그래서 보관함에는 주소만 담으면 된다 —
 * 큰 파일을 브라우저에 쟁여 두지 않는다.
 */
import type { BookDoc, ManifestEntry } from "./contentApi";

const KEY = (bookId: string) => `gc_admin_bookdraft_${bookId}`;

export interface BookDraft {
  doc: BookDoc;
  /** 이 책을 만들며 바꾼 그림 자리만(주소는 이미 엣지에 있다). */
  assets: Record<string, ManifestEntry>;
  at: number;
}

/** 브라우저 저장소는 유한하다 — 임시 dataURL(서버 미연결 시 폴백)은 담지 않는다. */
function slim(assets: Record<string, ManifestEntry>): Record<string, ManifestEntry> {
  const out: Record<string, ManifestEntry> = {};
  for (const [k, v] of Object.entries(assets)) {
    if (typeof v?.url === "string" && v.url.startsWith("data:")) continue;
    out[k] = v;
  }
  return out;
}

/** 저장. 실패해도 던지지 않는다 — 보관은 부가 기능이고, 편집을 막으면 본말이 뒤집힌다. */
export function saveBookDraft(bookId: string, doc: BookDoc, assets: Record<string, ManifestEntry>): boolean {
  if (!bookId) return false;
  try {
    localStorage.setItem(KEY(bookId), JSON.stringify({ doc, assets: slim(assets), at: Date.now() }));
    return true;
  } catch {
    return false; // 저장소가 꽉 찼거나 사생활 보호 모드
  }
}

export function loadBookDraft(bookId: string): BookDraft | null {
  try {
    const raw = localStorage.getItem(KEY(bookId));
    if (!raw) return null;
    const d = JSON.parse(raw) as BookDraft;
    return d && typeof d === "object" && d.doc ? d : null;
  } catch {
    return null; // 깨진 값 — 없는 것으로
  }
}

export function clearBookDraft(bookId: string): void {
  try {
    localStorage.removeItem(KEY(bookId));
  } catch {
    /* 무시 */
  }
}

/** "3분 전"처럼 사람이 읽는 시각 — 언제 저장한 건지 안 보이면 불러올지 말지 못 정한다. */
export function draftAgeText(at: number, now = Date.now()): string {
  const m = Math.floor((now - at) / 60000);
  if (m < 1) return "방금";
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 전`;
  return `${Math.floor(h / 24)}일 전`;
}
